import { Typography, Card, Skeleton, Box, Alert, CardContent } from '@mui/material';
import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';

import { MetricBarChart } from '@occc/ui-design-system';
import { barChartColor, darkBarChartColor } from '@occc-app/themes/colors';
import { ReimbursementsTimeSeriesDataPointFragment } from '@occc/graphql';
import { parseDate } from '@occc-app/lib/date';

import {
  CHART_HEIGHT,
  CHART_HEIGHT_WITH_LEGEND,
} from './retailer-network-tobacco-weekly-reimbursements.constants';

type RetailerNetworkTobaccoWeeklyReimbursementsProps = {
  data?: ReimbursementsTimeSeriesDataPointFragment[];
  loading?: boolean;
  error?: ApolloError;
};

export const RetailerNetworkTobaccoWeeklyReimbursements = ({
  data,
  loading,
  error,
}: RetailerNetworkTobaccoWeeklyReimbursementsProps) => {
  const metrics = useMemo(() => {
    return data?.map((item) => ({
      date: parseDate(item.date),
      value: {
        Loyalty: item.value.loyalty,
        'Multi Unit': item.value.multiUnit,
      },
    }));
  }, [data]);

  const renderContent = () => {
    if (loading) {
      return <Skeleton variant='rectangular' width='100%' height={CHART_HEIGHT_WITH_LEGEND} />;
    }

    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: CHART_HEIGHT_WITH_LEGEND,
          }}
        >
          <Alert severity='error'>
            Failed to load weekly reimbursements data. Please try again later.
          </Alert>
        </Box>
      );
    }

    if (!metrics || metrics.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: CHART_HEIGHT_WITH_LEGEND,
          }}
        >
          <Typography variant='body1'>No reimbursement data available</Typography>
        </Box>
      );
    }

    return (
      <MetricBarChart
        metrics={metrics}
        metricsType='currency'
        barColor={[barChartColor, darkBarChartColor]}
        isLegendVisible
        isBarStacked
        height={CHART_HEIGHT}
      />
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography variant='body1' fontWeight={500} sx={{ mb: 1 }}>
          Weekly Reimbursements
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          (All reimbursement values are estimates)
        </Typography>
        {renderContent()}
      </CardContent>
    </Card>
  );
};
